import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { Plus, Trash2, Shield } from 'lucide-react'

interface User {
  id: string
  email: string
  full_name: string
  role: string
  created_at: string
}

export default function AdminUsers() {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [newEmail, setNewEmail] = useState('')
  const [newRole, setNewRole] = useState('agent')
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    checkAdmin()
    fetchUsers()
  }, [])

  const checkAdmin = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (user) {
      const { data } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', user.id)
        .single()
      setIsAdmin(data?.role === 'admin')
    }
  }

  const fetchUsers = async () => {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .order('created_at', { ascending: false })

    if (!error) setUsers(data || [])
    setLoading(false)
  }

  const addUser = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Create user via admin API
    const { data: authData, error: authError } = await supabase.auth.admin.createUser({
      email: newEmail,
      password: 'TempPass123!',
      email_confirm: true,
    })

    if (authError) {
      alert('Error: ' + authError.message)
      return
    }

    if (authData.user) {
      // Set role
      await supabase.from('user_profiles').update({ role: newRole }).eq('id', authData.user.id)
      
      // Link to team
      await supabase.from('team_members').insert({
        team_id: 'ef6b2a6d-0237-4fb3-ad09-15dba4bf7fb2',
        user_id: authData.user.id,
        role: newRole
      })

      setNewEmail('')
      fetchUsers()
      alert('User created! Temporary password: TempPass123!')
    }
  }

  const removeUser = async (userId: string) => {
    if (!confirm('Remove this user?')) return
    
    await supabase.from('team_members').delete().eq('user_id', userId)
    await supabase.auth.admin.deleteUser(userId)
    fetchUsers()
  }

  if (!isAdmin) {
    return <div className="p-8">Access denied. Admin only.</div>
  }

  if (loading) return <div className="p-8">Loading...</div>

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold mb-6">User Management</h1>

      {/* Add User Form */}
      <form onSubmit={addUser} className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Plus size={20} />
          Add New User
        </h2>
        <div className="flex gap-4">
          <input
            type="email"
            placeholder="Email address"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            required
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
          />
          <select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="agent">Agent</option>
            <option value="va">VA</option>
            <option value="admin">Admin</option>
          </select>
          <button
            type="submit"
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
          >
            Add User
          </button>
        </div>
      </form>

      {/* Users List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Added</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id}>
                <td className="px-6 py-4">
                  <div className="font-medium text-gray-900">{user.full_name || '—'}</div>
                  <div className="text-sm text-gray-500">{user.email}</div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.role === 'admin' ? 'bg-red-100 text-red-800' :
                    user.role === 'va' ? 'bg-blue-100 text-blue-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {user.role === 'admin' && <Shield size={12} className="mr-1" />}
                    {user.role}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={() => removeUser(user.id)}
                    className="text-red-600 hover:text-red-800"
                    title="Remove user"
                  >
                    <Trash2 size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
